import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Box, Text, Flex, useColorModeValue, IconButton, HStack, Textarea, Button, Collapse, Progress, Circle } from '@chakra-ui/react';
import { FiThumbsUp, FiThumbsDown, FiPlay, FiPause, FiRefreshCw } from 'react-icons/fi';
import { RiRobot2Fill } from 'react-icons/ri';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import type { Components } from 'react-markdown';
import { useOutsideClick } from '@chakra-ui/react';

interface MessageProps {
  message: string | {
    content: string;
    conversation_id: string;
    message_id: number | string;
  };
  isUser: boolean;
  timestamp: string;
  onFeedback?: (messageId: number | string, rating: number, feedbackText?: string) => void;
}

// Add the helper function
const getMessageContent = (content: string | { content: string; conversation_id: string; message_id: number | string }): string => {
  return typeof content === 'string' ? content : content.content;
};

// Update the AudioPlayer to handle both local and S3 URLs
const AudioPlayer = ({ audioUrl, s3Key }: { audioUrl: string, s3Key?: string }) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [duration, setDuration] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const [audioError, setAudioError] = useState<string | null>(null);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const audioRef = useRef<HTMLAudioElement>(null);
  const baseUrl = process.env.REACT_APP_API_URL || 'http://localhost:8889';
  
  const accentColor = useColorModeValue('blue.500', 'blue.300');
  const bgColor = useColorModeValue('gray.100', 'gray.700');
  const progressBgColor = useColorModeValue('gray.200', 'gray.600');
  
  // Get the proper streaming URL
  const getProperAudioUrl = useCallback((originalUrl: string, s3KeyParam?: string) => {
    // If it's a blob URL (local recording), use it directly
    if (originalUrl.startsWith('blob:')) {
      return originalUrl;
    }
    
    // If it has an S3 key, use our streaming endpoint
    if (s3KeyParam) {
      const streamUrl = `${baseUrl}/api/stream-audio/${s3KeyParam}`;
      return streamUrl;
    }
    
    // If we have an S3 URL, extract the path part after the bucket name and use proxy endpoint
    if (originalUrl && (
        (originalUrl.includes('.s3.') && originalUrl.includes('amazonaws.com')) || 
        originalUrl.includes('it360bucket') 
      )) {
      // Try to extract using pattern for voice recordings first
      if (originalUrl.includes('voice_recordings/')) {
        const match = originalUrl.match(/voice_recordings\/[^?]+/);
        if (match) {
          const pathPart = match[0];
          const streamUrl = `${baseUrl}/api/stream-audio/${pathPart}`;
          return streamUrl;
        }
      }
      
      // Standard extraction from amazonaws URL
      const urlParts = originalUrl.split('.amazonaws.com/');
      if (urlParts.length > 1) {
        const pathPart = urlParts[1].split('?')[0]; // Remove query parameters
        
        // Check if we need to add voice_recordings/ prefix
        let finalPath = pathPart;
        if (!finalPath.startsWith('voice_recordings/')) {
          const uuidPattern = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}\.(mp3|mp4|wav|webm|ogg)$/i;
          if (uuidPattern.test(finalPath)) {
            finalPath = `voice_recordings/${finalPath}`;
          }
        }
        
        const streamUrl = `${baseUrl}/api/stream-audio/${finalPath}`;
        return streamUrl;
      }
      
      // For URLs that might be formatted differently
      // Extract the path by looking for common patterns in S3 URLs
      const matches = originalUrl.match(/it360bucket[^\/]*\/([^?]+)/);
      if (matches && matches[1]) {
        const pathPart = matches[1];
        const streamUrl = `${baseUrl}/api/stream-audio/${pathPart}`;
        return streamUrl;
      }
    }
    
    // For presigned URLs that point directly to S3
    if (originalUrl && originalUrl.includes('X-Amz-SignedHeaders')) {
      // This is very likely a presigned URL - extract the path
      const matches = originalUrl.match(/https:\/\/[^\/]+\/([^?]+)/);
      if (matches && matches[1]) {
        const pathPart = matches[1];
        const streamUrl = `${baseUrl}/api/stream-audio/${pathPart}`;
        return streamUrl;
      }
    }
    
    // Otherwise use the original URL
    return originalUrl;
  }, [baseUrl]);
  
  // Set the audio source on component mount and when URL changes
  useEffect(() => {
    const setupAudio = () => {
      // Reset error state when URL changes
      setAudioError(null);
      
      try {
        // Get the streaming URL
        const streamUrl = getProperAudioUrl(audioUrl, s3Key);
        
        // Set the audio source if we have a ref
        if (audioRef.current) {
          audioRef.current.src = streamUrl;
          audioRef.current.load();
        }
      } catch (err) {
        console.error("Error setting up audio:", err);
        setAudioError(`Error setting up audio: ${err instanceof Error ? err.message : 'Unknown error'}`);
      }
    };
    
    setupAudio();
  }, [audioUrl, s3Key, getProperAudioUrl]);
  
  const formatTime = (time: number) => {
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${minutes}:${seconds < 10 ? '0' : ''}${seconds}`;
  };
  
  const handlePlayPause = () => {
    if (audioRef.current) {
      if (isPlaying) {
        audioRef.current.pause();
      } else {
        // Attempt to play and catch any errors
        audioRef.current.play().catch(err => {
          console.error("Error playing audio:", err);
          setAudioError(`Playback error: ${err.message}`);
        });
      }
      setIsPlaying(!isPlaying);
    }
  };
  
  const handleTimeUpdate = () => {
    if (audioRef.current) {
      setCurrentTime(audioRef.current.currentTime);
      setProgress((audioRef.current.currentTime / audioRef.current.duration) * 100);
    }
  };
  
  const handleProgressClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (audioRef.current) {
      const progressBar = e.currentTarget.getBoundingClientRect();
      const clickPosition = e.clientX - progressBar.left;
      const percentClicked = (clickPosition / progressBar.width);
      const newTime = percentClicked * audioRef.current.duration;
      
      audioRef.current.currentTime = newTime;
      setProgress(percentClicked * 100);
    }
  };
  
  const handleLoadedMetadata = () => {
    if (audioRef.current) {
      setDuration(audioRef.current.duration);
    }
  };
  
  const handleEnded = () => {
    setIsPlaying(false);
    setProgress(0);
    setCurrentTime(0);
  };

  const handleError = (e: React.SyntheticEvent<HTMLAudioElement>) => {
    console.error("Audio element error:", e);
    const audioElement = e.currentTarget;
    
    let errorMessage = "Unable to play audio - unknown error";
    
    // Parse the error code to provide a more helpful message
    if (audioElement.error) {
      switch (audioElement.error.code) {
        case 1:
          errorMessage = "Audio playback aborted";
          break;
        case 2:
          errorMessage = "Network error - unable to download audio";
          break;
        case 3:
          errorMessage = "Audio decoding failed - format may be unsupported";
          break;
        case 4:
          errorMessage = "Audio source not found or access denied";
          break;
        default:
          errorMessage = `Error code ${audioElement.error.code}: ${audioElement.error.message}`;
      }
    }
    
    // Check if it might be a CORS issue with S3
    if (audioUrl.includes('.s3.') && audioUrl.includes('amazonaws.com')) {
      errorMessage += ". This may be a CORS policy issue with the S3 bucket.";
    }
    
    setAudioError(errorMessage);
    setIsPlaying(false);
  };
  
  // Handler for refreshing pre-signed URL
  const handleRefreshUrl = useCallback(() => {
    if (!s3Key) {
      setAudioError("Cannot refresh URL - missing S3 key information");
      return;
    }
    
    setIsRefreshing(true);
    try {
      // Get a fresh streaming URL
      const streamUrl = getProperAudioUrl(audioUrl, s3Key);
      
      // Update the audio element with the new URL
      if (audioRef.current) {
        const wasPlaying = !audioRef.current.paused;
        const currentPosition = audioRef.current.currentTime;
        
        // Set the new URL
        audioRef.current.src = streamUrl;
        
        // After setting new source, we need to reload and seek to previous position
        audioRef.current.load();
        
        // Try to set the current position, but handle errors
        try {
          audioRef.current.currentTime = currentPosition;
        } catch (posErr) {
          console.warn("Could not set currentTime:", posErr);
          // Don't let this error prevent playback
        }
        
        // If it was playing before, start playing again after a short delay
        if (wasPlaying) {
          // Need to wait a bit for the audio to be ready
          setTimeout(() => {
            if (audioRef.current) {
              audioRef.current.play().catch(err => {
                console.error("Error resuming playback after refresh:", err);
                setAudioError(`Playback error after refresh: ${err.message}`);
              });
            }
          }, 500); // Increased delay for better reliability
        }
        
        // Clear error state
        setAudioError(null);
      }
    } catch (error) {
      console.error('Error during URL refresh:', error);
      setAudioError(`Error refreshing URL: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setIsRefreshing(false);
    }
  }, [s3Key, audioUrl, getProperAudioUrl]);
  
  // Generate random bar heights for the waveform visualization
  const generateWaveform = (barCount: number) => {
    const bars = [];
    // Create a more natural wave pattern
    for (let i = 0; i < barCount; i++) {
      // Create a more natural wave pattern with smoother transitions between heights
      // Adding some sine wave influence for a more pleasing pattern
      const base = 25 + Math.sin(i * 0.5) * 15; // base height with sine wave
      const random = Math.random() * 50; // random variation
      const height = Math.min(95, Math.max(20, base + random)); // keep between 20-95%
      bars.push(height);
    }
    return bars;
  };
  
  // Generate waveform data once using useMemo, reduce number of bars for performance
  const waveformBars = React.useMemo(() => generateWaveform(32), []);
  
  return (
    <Box 
      borderRadius="lg" 
      bg={bgColor} 
      p={3} 
      width="100%"
      boxShadow="sm"
    >
      <audio 
        ref={audioRef}
        crossOrigin="anonymous"
        preload="metadata"
        onTimeUpdate={handleTimeUpdate}
        onLoadedMetadata={handleLoadedMetadata}
        onEnded={handleEnded}
        onError={handleError}
        style={{ display: 'none' }}
      />
      
      {audioError ? (
        <Box color="red.500" fontSize="sm" mb={2} p={2} bg="red.50" borderRadius="md">
          <Text fontWeight="bold">Audio Error:</Text>
          <Text>{audioError}</Text>
          <Text mt={1} fontSize="xs">
            Source: {audioUrl.substring(0, 50)}{audioUrl.length > 50 ? '...' : ''}
          </Text>
          
          {/* Add refresh button if we have the S3 key */}
          {s3Key && (
            <Button
              leftIcon={<FiRefreshCw />}
              size="sm"
              mt={2}
              colorScheme="blue"
              isLoading={isRefreshing}
              loadingText="Refreshing"
              onClick={handleRefreshUrl}
            >
              Refresh URL
            </Button>
          )}
        </Box>
      ) : (
        <Flex align="center" mb={2}>
          <Circle 
            size="40px" 
            bg={accentColor} 
            color="white" 
            cursor="pointer"
            onClick={handlePlayPause}
            _hover={{ opacity: 0.8 }}
            transition="all 0.2s"
          >
            {isPlaying ? <FiPause size="20px" /> : <FiPlay size="20px" style={{ marginLeft: '2px' }} />}
          </Circle>
          
          <Box ml={3} flex="1">
            {/* Improved waveform visualization */}
            <Box 
              position="relative" 
              height="40px" 
              cursor="pointer"
              onClick={handleProgressClick}
              display="flex"
              alignItems="center"
            >
              {/* Waveform bars with improved style */}
              <Flex 
                width="100%" 
                height="100%" 
                alignItems="center" 
                justifyContent="space-between"
                position="relative"
                overflow="hidden"
              >
                {waveformBars.map((height, index) => (
                  <Box
                    key={index}
                    height={`${height}%`}
                    width="6px"
                    borderRadius="full"
                    mx="1px"
                    bg={index / waveformBars.length * 100 <= progress ? accentColor : progressBgColor}
                    transition="all 0.3s ease"
                    opacity={isPlaying && Math.random() > 0.5 ? (0.7 + Math.random() * 0.3) : 1}
                    transform={`scaleY(${isPlaying ? (0.95 + Math.random() * 0.1) : 1})`}
                  />
                ))}
                
                {/* Interactive hover highlight effect */}
                <Box
                  position="absolute"
                  left="0"
                  top="0"
                  right="0"
                  bottom="0"
                  pointerEvents="none"
                  bgGradient={`linear(to-r, ${accentColor}00 ${progress}%, ${progressBgColor}40 ${progress}%, ${progressBgColor}00 ${progress + 5}%)`}
                />
                
                {/* Smoother progress indicator with glow effect */}
                <Box
                  position="absolute"
                  left={`${progress}%`}
                  height="100%"
                  width="2px"
                  bg="white"
                  zIndex={3}
                  display={progress > 0 ? "block" : "none"}
                  transform="translateX(-50%)"
                  boxShadow={`0 0 8px ${accentColor}, 0 0 4px ${accentColor}`}
                />
              </Flex>
            </Box>
            
            <Flex justify="space-between" fontSize="xs" mt={1}>
              <Text>{formatTime(currentTime)}</Text>
              <Text>{Number.isFinite(duration) ? formatTime(duration) : '--:--'}</Text>
            </Flex>
          </Box>
        </Flex>
      )}
    </Box>
  );
};

const Message: React.FC<MessageProps> = ({ message, isUser, timestamp, onFeedback }) => {
  const [feedback, setFeedback] = useState<number | null>(null);
  const [showFeedbackInput, setShowFeedbackInput] = useState(false);
  const [feedbackText, setFeedbackText] = useState('');
  const feedbackRef = useRef<HTMLDivElement>(null);
  const textColor = useColorModeValue('gray.800', 'gray.100');
  
  // Add these color values at the component level for transcription styling
  const transcriptionTextColor = useColorModeValue('gray.600', 'gray.300');
  const transcriptionBgColor = useColorModeValue('gray.50', 'gray.800');
  const userBgColor = useColorModeValue('gray.100', 'gray.700');

  // Get message ID if available
  const messageId = typeof message === 'object' ? message.message_id : null;

  // Handle clicking outside the feedback box
  useOutsideClick({
    ref: feedbackRef,
    handler: () => {
      if (showFeedbackInput) {
        setShowFeedbackInput(false);
        setFeedbackText('');
      }
    },
  });

  const handleFeedback = (rating: number) => {
    if (messageId && onFeedback) {
      setFeedback(rating);
      if (rating === -1) {
        setShowFeedbackInput(true);
        // Send the negative feedback immediately
        onFeedback(messageId, rating);
      } else {
        onFeedback(messageId, rating);
      }
    }
  };

  const handleSubmitFeedback = () => {
    if (messageId && onFeedback && feedbackText.trim()) {
      // Only send feedback text if user wrote something
      onFeedback(messageId, -1, feedbackText);
      setShowFeedbackInput(false);
      setFeedbackText('');
    } else {
      // Just close the feedback input if no text
      setShowFeedbackInput(false);
      setFeedbackText('');
    }
  };

  const components: Partial<Components> = {
    p: ({children}) => (
      <Text mb={3} lineHeight="tall" fontSize="md" whiteSpace="pre-wrap">
        {children}
      </Text>
    ),
    h3: ({children}) => (
      <Text fontSize="md" fontWeight="bold" mb={2} mt={2}>
        {children}
      </Text>
    ),
    ul: ({children}) => (
      <Box as="ul" pl={4} mb={3} fontSize="md" listStyleType="disc">
        {children}
      </Box>
    ),
    ol: ({children}) => (
      <Box as="ol" pl={4} mb={3} fontSize="md" listStyleType="decimal">
        {children}
      </Box>
    ),
    li: ({children}) => (
      <Box as="li" mb={2}>
        {children}
      </Box>
    ),
    strong: ({children}) => (
      <Text as="span" fontWeight="bold">
        {children}
      </Text>
    )
  };

  const formatTimestamp = (timestamp: string) => {
    try {
      // Handle PostgreSQL timestamp format (missing Z at the end)
      let dateObj;
      
      if (timestamp && !timestamp.endsWith('Z') && timestamp.includes('T')) {
        // Add 'Z' to explicitly mark as UTC
        dateObj = new Date(timestamp + 'Z');
      } else {
        // Standard ISO format or other format
        dateObj = new Date(timestamp);
      }
      
      // Verify the date object is valid
      if (isNaN(dateObj.getTime())) {
        console.error('Failed to create valid date from:', timestamp);
        return 'Invalid date';
      }
      
      // Format in user's local timezone using Intl.DateTimeFormat for better localization
      const formattedTime = new Intl.DateTimeFormat(navigator.language || 'en-US', {
        hour: '2-digit',
        minute: '2-digit',
        hour12: true // AM/PM format
      }).format(dateObj);
      
      return formattedTime;
    } catch (error) {
      console.error('Error formatting timestamp:', error);
      return 'Time error';
    }
  };

  // Function to check if the message is an audio message
  const isAudioMessage = () => {
    if (typeof message !== 'string') {
      return false;
    }
    
    try {
      const parsedContent = JSON.parse(message);
      return parsedContent && parsedContent.type === 'audio';
    } catch (e) {
      return false;
    }
  };
  
  // Function to render audio message content with new player
  const renderAudioMessage = () => {
    if (typeof message !== 'string') {
      return <Text>Error displaying audio message: Invalid message format</Text>;
    }
    
    try {
      const parsedContent = JSON.parse(message);
      
      // Log the entire parsed content for debugging
      console.log("Audio message content:", parsedContent);
      
      // Extract the key properties
      const audioUrl = parsedContent.presigned_url || parsedContent.s3Url || parsedContent.audioUrl || null;
      
      // Get the S3 key - either from the message directly or by extracting path from S3 URL
      let s3Key = parsedContent.s3_key || null;
      
      // console.log("Initial S3 key from message:", s3Key);
      // console.log("Audio URL:", audioUrl);
      
      // If we don't have an s3_key but have an S3 URL, extract the path part
      if (!s3Key && parsedContent.s3Url) {
        if (parsedContent.s3Url.includes('voice_recordings/')) {
          // Extract the entire path starting with 'voice_recordings/'
          const match = parsedContent.s3Url.match(/voice_recordings\/[^?]+/);
          if (match) {
            s3Key = match[0];
            console.log("Extracted S3 key from URL pattern:", s3Key);
          }
        } else if (parsedContent.s3Url.includes('.amazonaws.com/')) {
          // Extract the path after the bucket domain
          // URL format: https://bucketname.s3.region.amazonaws.com/folder/filename.ext
          const urlParts = parsedContent.s3Url.split('.amazonaws.com/');
          if (urlParts.length > 1) {
            s3Key = urlParts[1].split('?')[0]; // Remove any query parameters
            console.log("Extracted S3 key from amazonaws URL:", s3Key);
          }
        }
      }
      
      // Add voice_recordings/ prefix if the S3 key doesn't already have it
      // This is crucial for the backend to find the file in the correct folder
      if (s3Key && !s3Key.startsWith('voice_recordings/')) {
        // Check if it's a UUID-like filename (the pattern matches our audio files)
        const uuidPattern = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}\.(mp3|mp4|wav|webm|ogg)$/i;
        if (uuidPattern.test(s3Key)) {
          s3Key = `voice_recordings/${s3Key}`;
          console.log(`Added voice_recordings/ prefix to S3 key: ${s3Key}`);
        }
      }
      
      if (!audioUrl) {
        return (
          <Text color="red.500">
            Error: No valid audio URL found in message
          </Text>
        );
      }
      
      // console.log(`Final audio info - URL: ${audioUrl}, S3 key: ${s3Key || 'none'}`);
      
      return (
        <Box>
          {/* Modern Audio player */}
          <Box mb={2}>
            <AudioPlayer 
              audioUrl={audioUrl} 
              s3Key={s3Key} // Pass the S3 key for streaming
            />
          </Box>
          
          {/* Show audio type for debugging */}
          {/* <Text fontSize="xs" color="gray.500" mb={1}>
            Source: {sourceType} audio
          </Text> */}
          
          {/* Transcription in italics - only shown if available */}
          {parsedContent.transcription && (
            <Text 
              as="i" 
              fontSize="sm" 
              color={transcriptionTextColor}
              bg={transcriptionBgColor}
              p={2}
              borderRadius="md"
              display="block"
            >
              {parsedContent.transcription}
            </Text>
          )}
          
          {/* Show placeholder text if no transcription yet */}
          {!parsedContent.transcription && (
            <Text 
              fontSize="sm" 
              color="gray.500"
              display="flex"
              alignItems="center"
            >
              <Box 
                as="span" 
                mr={2}
                animation="pulse 1.5s infinite" 
                sx={{
                  "@keyframes pulse": {
                    "0%, 100%": { opacity: 0.5 },
                    "50%": { opacity: 1 }
                  }
                }}
              >
                ●
              </Box>
              Transcribing audio...
            </Text>
          )}
        </Box>
      );
    } catch (e) {
      console.error("Error parsing audio message:", e, message);
      return <Text>Error parsing audio message</Text>;
    }
  };

  return (
    <Flex 
      w="100%" 
      mb={2} 
      direction="column"
      py={1.5}
    >
      <Flex
        maxW="3xl"
        mx="auto"
        w="100%"
        px={4}
        alignItems="flex-start"
        justifyContent={isUser ? 'flex-end' : 'flex-start'}
      >
        {!isUser && (
          <Box
            mr={3}
            mt={1}
            color="blue.500"
            fontSize="24px"
          >
            <RiRobot2Fill />
          </Box>
        )}
        
        <Box 
          maxW={isUser ? "75%" : "85%"}
          p={isUser ? 3 : 0}
          px={isUser ? 4 : 0}
          bg={isUser ? userBgColor : 'transparent'}
          borderRadius={isUser ? "2xl" : undefined}
        >
          {/* Render content based on message type */}
          {isAudioMessage() ? (
            renderAudioMessage()
          ) : (
            // Regular message rendering
            <Box className="markdown-content" color={textColor}>
              <ReactMarkdown remarkPlugins={[remarkGfm]} components={components}>
                {getMessageContent(message)}
              </ReactMarkdown>
            </Box>
          )}
          
          {!isUser && messageId && (
            <>
              <Flex justify="space-between" align="center" mt={4}>
                <Text fontSize="xs" color="gray.500" opacity={0.8}>
                  {formatTimestamp(timestamp)}
                </Text>
                <HStack spacing={1}>
                  <IconButton
                    aria-label="Thumbs up"
                    icon={<FiThumbsUp />}
                    size="xs"
                    variant="ghost"
                    colorScheme={feedback === 1 ? "green" : "gray"}
                    onClick={() => handleFeedback(1)}
                  />
                  <IconButton
                    aria-label="Thumbs down"
                    icon={<FiThumbsDown />}
                    size="xs"
                    variant="ghost"
                    colorScheme={feedback === -1 ? "red" : "gray"}
                    onClick={() => handleFeedback(-1)}
                  />
                </HStack>
              </Flex>

              <Collapse in={showFeedbackInput} animateOpacity>
                <Box mt={4} ref={feedbackRef}>
                  <Textarea
                    placeholder="Please let us know what could be improved (optional)..."
                    value={feedbackText}
                    onChange={(e) => setFeedbackText(e.target.value)}
                    size="sm"
                    resize="vertical"
                    mb={2}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && !e.shiftKey) {
                        e.preventDefault();
                        handleSubmitFeedback();
                      }
                      if (e.key === 'Escape') {
                        setShowFeedbackInput(false);
                        setFeedbackText('');
                      }
                    }}
                  />
                  <Flex justify="flex-end" gap={2}>
                    <Button
                      size="xs"
                      variant="ghost"
                      onClick={() => {
                        setShowFeedbackInput(false);
                        setFeedbackText('');
                      }}
                    >
                      Cancel
                    </Button>
                    <Button
                      size="xs"
                      colorScheme="blue"
                      onClick={handleSubmitFeedback}
                    >
                      Submit Feedback
                    </Button>
                  </Flex>
                </Box>
              </Collapse>
            </>
          )}
          
          {isUser && (
            <Box></Box>
          )}
        </Box>
      </Flex>
    </Flex>
  );
};

export default Message; 